import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { arrayMove } from "@dnd-kit/sortable";
import { api } from "../lib/api";
import { defaultPrescription, applyAssessmentToBlueprint } from "../lib/rescale";
import { autoAssignSection } from "../lib/sections";

export const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const emptyDay = (i) => ({ day: i + 1, label: `${DAY_LABELS[i]} - Train`, focus: "Strength", exercises: [] });
const emptyWeek = (idx) => ({ week_index: idx, intent: "Build", days: Array.from({ length: 7 }, (_, i) => emptyDay(i)) });

export function usePlanBuilder(id) {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [assessment, setAssessment] = useState(null);

  useEffect(() => {
    api.get("/me/assessment").then((r) => setAssessment(r.data || {})).catch(() => setAssessment({}));
  }, []);

  useEffect(() => {
    if (id) {
      api.get(`/plans/${id}`).then((r) => setPlan(r.data));
    } else {
      setPlan({ name: "New Microcycle", type: "microcycle", goal: "", level: "Intermediate", weeks: [emptyWeek(1)], completions: {}, is_public: false });
    }
  }, [id]);

  const update = useCallback((patch) => setPlan((p) => ({ ...p, ...patch })), []);

  const addWeek = useCallback(() => {
    setPlan((p) => {
      const next = [...p.weeks, emptyWeek(p.weeks.length + 1)];
      return { ...p, weeks: next, type: next.length > 1 ? "mesocycle" : "microcycle" };
    });
  }, []);

  const removeWeek = useCallback((i) => {
    setPlan((p) => {
      const next = p.weeks.filter((_, idx) => idx !== i).map((w, idx) => ({ ...w, week_index: idx + 1 }));
      return { ...p, weeks: next, type: next.length > 1 ? "mesocycle" : "microcycle" };
    });
  }, []);

  const updateWeekIntent = useCallback((wi, intent) => {
    setPlan((p) => ({ ...p, weeks: p.weeks.map((x, i) => (i === wi ? { ...x, intent } : x)) }));
  }, []);

  const updateDay = useCallback((wi, di, patch) => {
    setPlan((p) => ({
      ...p,
      weeks: p.weeks.map((w, i) => (i !== wi ? w : { ...w, days: w.days.map((d, j) => (j !== di ? d : { ...d, ...patch })) })),
    }));
  }, []);

  const removeExercise = useCallback((wi, di, exIdx) => {
    setPlan((p) => ({
      ...p,
      weeks: p.weeks.map((w, i) => i !== wi ? w : {
        ...w,
        days: w.days.map((d, j) => j !== di ? d : { ...d, exercises: d.exercises.filter((_, k) => k !== exIdx) }),
      }),
    }));
  }, []);

  const updateExercise = useCallback((wi, di, exIdx, patch) => {
    setPlan((p) => ({
      ...p,
      weeks: p.weeks.map((w, i) => i !== wi ? w : {
        ...w,
        days: w.days.map((d, j) => j !== di ? d : { ...d, exercises: d.exercises.map((e, k) => (k !== exIdx ? e : { ...e, ...patch })) }),
      }),
    }));
  }, []);

  const addExercise = useCallback((wi, di, exIdOrDef) => {
    setPlan((p) => {
      const exDef = typeof exIdOrDef === "object" && exIdOrDef ? exIdOrDef : null;
      const exId = exDef ? exDef.id : exIdOrDef;
      const baseEx = exDef
        ? defaultPrescription(exDef, assessment, p?.level || "Intermediate")
        : { exercise_id: exId, sets: 3, reps: "8-10", notes: "" };
      // Auto-bucket plyos into warm-up and stretches into cool-down. Users
      // can re-bucket manually via the section pill on the exercise row.
      const newEx = { ...baseEx, section: autoAssignSection(exId, exDef) };
      return {
        ...p,
        weeks: p.weeks.map((w, i) => i !== wi ? w : {
          ...w,
          days: w.days.map((d, j) => j !== di ? d : { ...d, exercises: [...d.exercises, newEx] }),
        }),
      };
    });
  }, [assessment]);

  const reorderExercises = useCallback((wi, di, oldIdx, newIdx) => {
    setPlan((p) => ({
      ...p,
      weeks: p.weeks.map((w, i) => i !== wi ? w : {
        ...w,
        days: w.days.map((d, j) => j !== di ? d : { ...d, exercises: arrayMove(d.exercises, oldIdx, newIdx) }),
      }),
    }));
  }, []);

  const replaceExercises = useCallback((wi, di, newExercises, dayPatch = {}) => {
    setPlan((p) => {
      const filled = applyAssessmentToBlueprint(newExercises, assessment, p?.level || "Intermediate");
      return {
        ...p,
        weeks: p.weeks.map((w, i) => i !== wi ? w : {
          ...w,
          days: w.days.map((d, j) => j !== di ? d : { ...d, ...dayPatch, exercises: filled }),
        }),
      };
    });
  }, [assessment]);

  const toggleComplete = useCallback(async (wi, di) => {
    if (!id) { alert("Save the plan first to track completions."); return; }
    const w = plan.weeks[wi];
    const d = w.days[di];
    const key = `${w.week_index}-${d.day}`;
    const completed = !(plan.completions || {})[key];
    const { data } = await api.post(`/plans/${id}/complete`, { week_index: w.week_index, day: d.day, completed });
    setPlan((p) => ({ ...p, completions: data.completions }));
  }, [id, plan]);

  const toggleShare = useCallback(async () => {
    if (!id) { alert("Save the plan first to share it."); return; }
    const next = !plan.is_public;
    const { data } = await api.post(`/plans/${id}/share`, { is_public: next });
    setPlan((p) => ({ ...p, is_public: data.is_public, share_slug: data.share_slug }));
  }, [id, plan]);

  const progressWeek = useCallback(async (fromWeekIndex, weekType = "progress") => {
    if (!id) { alert("Save the plan first to progress weeks."); return; }
    try {
      const { data } = await api.post(`/plans/${id}/progress-week`, { from_week_index: fromWeekIndex, week_type: weekType });
      setPlan(data);
    } catch (e) {
      alert("Progression failed: " + (e.response?.data?.detail || e.message));
    }
  }, [id]);

  const save = useCallback(async () => {
    const payload = { name: plan.name, type: plan.type, goal: plan.goal, level: plan.level, weeks: plan.weeks };
    try {
      if (id) {
        await api.put(`/plans/${id}`, payload);
      } else {
        const { data } = await api.post("/plans", payload);
        navigate(`/builder/${data.id}`, { replace: true });
      }
      alert("Plan saved");
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    }
  }, [id, plan, navigate]);

  return {
    plan, setPlan, assessment,
    update, addWeek, removeWeek, updateWeekIntent, updateDay,
    addExercise, removeExercise, updateExercise, reorderExercises, replaceExercises,
    toggleComplete, toggleShare, progressWeek, save,
  };
}
